import React, { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const ROLE_TARGETS = {
  admin: "/app",
  manager: "/app",
  cashier: "/app/pos",
  waiter: "/app/tables",
  kitchen: "/app/kds",
  customer: "/menu",
};

const QUICK_LOGINS = [
  { label: "Admin",   email: "admin@resto.com",    password: "admin123" },
  { label: "Manager", email: "manager@resto.com",  password: "manager123" },
  { label: "Cashier", email: "cashier@resto.com",  password: "cashier123" },
  { label: "Kitchen", email: "kitchen@resto.com",  password: "kitchen123" },
  { label: "Waiter",  email: "waiter@resto.com",   password: "waiter123" },
];

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const doLogin = async (e, qe, qp) => {
    if (e?.preventDefault) e.preventDefault();
    const em = qe ?? email;
    const pw = qp ?? password;
    setBusy(true);
    setErr(null);
    try {
      const u = await login(em, pw);
      toast.success(`Welcome, ${u.name}`);
      const target = location.state?.from || ROLE_TARGETS[u.role] || "/app";
      nav(target, { replace: true });
    } catch (e2) {
      setErr(e2.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2" data-testid="login-page">
      {/* Left visual */}
      <div className="hidden lg:block relative">
        <img
          src="https://images.unsplash.com/photo-1613274554329-70f997f5789f?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Restaurant"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-[#2D2825]/55" />
        <div className="relative z-10 h-full flex flex-col justify-between p-12 text-white">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-lg bg-[var(--brand)] grid place-items-center font-display font-black text-xl">R</div>
            <div>
              <div className="font-display font-extrabold text-xl leading-none">Resto Manager</div>
              <div className="label-eyebrow text-[10px] mt-1 text-white/70">Enterprise POS Platform</div>
            </div>
          </div>
          <div>
            <h2 className="font-display font-black text-4xl leading-tight tracking-tight mb-3">
              Every table.<br/>Every order.<br/>One platform.
            </h2>
            <p className="text-white/75 text-base max-w-md leading-relaxed">
              Modern POS, real-time kitchen display, inventory, employees and analytics — built for restaurants that take their craft seriously.
            </p>
          </div>
          <div className="text-xs text-white/60">© Resto Manager · v1.0</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md">
          <div className="label-eyebrow mb-3">Sign in</div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight mb-2">Welcome back</h1>
          <p className="text-sm text-[var(--text-secondary)] mb-8">Use one of the seeded staff accounts below, or sign in with your credentials.</p>

          <form onSubmit={doLogin} className="space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="label-eyebrow">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1"
                placeholder="you@resto.com"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <Label htmlFor="password" className="label-eyebrow">Password</Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1"
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>
            {err && <div className="text-sm text-[var(--brand)] font-medium" data-testid="login-error">{err}</div>}
            <Button
              type="submit"
              disabled={busy}
              className="w-full btn-primary h-11 rounded-md font-semibold"
              data-testid="login-submit-btn"
            >
              {busy ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <div className="my-6 flex items-center gap-3">
            <div className="flex-1 h-px bg-[#EAE7E0]" />
            <div className="label-eyebrow text-[10px]">Quick demo logins</div>
            <div className="flex-1 h-px bg-[#EAE7E0]" />
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {QUICK_LOGINS.map((q) => (
              <button
                key={q.email}
                type="button"
                onClick={() => doLogin(null, q.email, q.password)}
                data-testid={`quick-login-${q.label.toLowerCase()}`}
                className="tile px-3 py-2.5 text-xs font-semibold text-[var(--text-primary)] hover:border-[var(--brand)]"
              >
                {q.label}
              </button>
            ))}
          </div>

          <div className="mt-8 text-sm text-[var(--text-secondary)]">
            New customer?{" "}
            <Link to="/signup" className="text-[var(--brand)] font-semibold hover:underline" data-testid="goto-signup">
              Create an account
            </Link>
            <span className="mx-2">·</span>
            <Link to="/menu" className="text-[var(--brand)] font-semibold hover:underline" data-testid="goto-public-menu">
              Browse menu
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
